import React, { useState } from 'react';
import { Check, Minus, ChevronDown, ArrowRight } from 'lucide-react';
import { LandingChrome } from './LandingChrome';
import { Nav, Footer, PLANS, type LandingPageProps } from './LandingPage';

/**
 * /pricing — a standalone page, not just the landing page's pricing strip.
 *
 * Paddle requires a published pricing page before it will approve a domain, and
 * it is the page people link to and return to when deciding. It carries the
 * full comparison and the billing questions that would clutter the home page.
 */

type PlanKey = 'Free' | 'Pro' | 'Team';

/** The full matrix. The landing page's cards are a summary of this. */
const ROWS: Array<{ group: string; items: Array<{ label: string; free: string | boolean; pro: string | boolean; team: string | boolean }> }> = [
  {
    group: 'Your calendar',
    items: [
      { label: 'Events', free: 'Unlimited', pro: 'Unlimited', team: 'Unlimited' },
      { label: 'Tasks and boards', free: 'Unlimited', pro: 'Unlimited', team: 'Unlimited' },
      { label: 'Tags', free: 'Unlimited', pro: 'Unlimited', team: 'Unlimited' },
      { label: 'Categories', free: '8', pro: 'Unlimited', team: 'Unlimited' },
      { label: 'All six views', free: true, pro: true, team: true },
      { label: 'Print-ready year', free: true, pro: true, team: true },
      { label: 'Export to ICS and JSON', free: true, pro: true, team: true },
    ],
  },
  {
    group: 'Bringing calendars in',
    items: [
      { label: 'Google Calendar, two-way', free: true, pro: true, team: true },
      { label: 'Imported calendars', free: '1', pro: '5', team: 'Unlimited' },
      { label: 'Public subscription links', free: false, pro: 'Unlimited', team: 'Unlimited' },
    ],
  },
  {
    group: 'Sharing',
    items: [
      { label: 'Shared spaces', free: '1', pro: '3', team: 'Unlimited' },
      { label: 'People per space', free: '2', pro: '5', team: 'Unlimited' },
      { label: 'Admin, editor and viewer roles', free: true, pro: true, team: true },
      { label: 'Task assignment', free: true, pro: true, team: true },
    ],
  },
  {
    group: 'Support and billing',
    items: [
      { label: 'Email support', free: true, pro: true, team: true },
      { label: 'Priority support', free: false, pro: true, team: true },
      { label: 'Invoiced billing', free: false, pro: false, team: true },
    ],
  },
];

const BILLING_FAQ = [
  {
    q: 'Is the free plan a trial?',
    a: 'No. It is free indefinitely, with no card required and no expiry. Unlimited events, tasks and tags on your own calendar, all six views, print and export. You pay only when you want more shared spaces, more people in them, or more imported calendars.',
  },
  {
    q: 'What happens if I stop paying?',
    a: 'Nothing is deleted. Your account reverts to the free plan and every event, task and calendar you created stays exactly where it is. Only the paid limits apply again — extra shared spaces become read-only rather than disappearing.',
  },
  {
    q: 'Can I get a refund?',
    a: 'Yes. If a paid plan is not right for you, tell us within 14 days of the charge and we will refund it in full, no reason needed. That applies to renewals too, not just the first payment.',
  },
  {
    q: 'Is tax included?',
    a: 'Yes. Prices include any VAT or sales tax that applies to you, calculated at checkout from your location. Payments are handled by Paddle, our merchant of record, who issue the invoice — so their name, not ours, appears on your statement.',
  },
  {
    q: 'How does Team billing work?',
    a: 'Per person, per month, with a minimum of three. You are billed for the seats you have, and can add or remove people at any time. Invoiced billing is available if your finance team needs it.',
  },
  {
    q: 'Can I switch plans?',
    a: 'Any time, in both directions. Upgrades take effect immediately and are charged pro rata. Downgrades take effect at the end of the period you have already paid for, so you never lose time you have bought.',
  },
];

function Cell({ value }: { value: string | boolean }) {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-[var(--lp-accent)]" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-[var(--lp-low)]" />;
  return <span className="text-[13px] text-[var(--lp-mid)]">{value}</span>;
}

export default function PricingPage({ onGoToLogin, onGoToSignUp, isLoggedIn }: LandingPageProps) {
  const [annual, setAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <LandingChrome>
      <Nav onGoToLogin={onGoToLogin} onGoToSignUp={onGoToSignUp} isLoggedIn={isLoggedIn} />

      <main>
        {/* ── Header ─────────────────────────────────────────────── */}
        <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
          <div className="mx-auto max-w-2xl px-5 text-center">
            <p className="eyebrow" style={{ color: 'var(--t-sky)' }}>Pricing</p>
            <h1 className="mt-3 text-balance text-4xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-5xl">
              Free for your own year.
              <br />
              Paid when you share it.
            </h1>
            <p className="mx-auto mt-5 max-w-lg text-[15px] leading-relaxed text-[var(--lp-mid)]">
              We never cap the events or tasks you create. What you make stays
              yours, and stays visible, whatever you pay.
            </p>

            <div className="mt-9 inline-flex items-center gap-1 rounded-lg border border-[var(--lp-grout)] bg-[var(--lp-raise)] p-1">
              <button
                type="button" onClick={() => setAnnual(false)} aria-pressed={!annual}
                className={`rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                  !annual ? 'bg-[var(--lp-raise-2)] text-[var(--lp-ink)]' : 'text-[var(--lp-mid)]'
                }`}
              >
                Monthly
              </button>
              <button
                type="button" onClick={() => setAnnual(true)} aria-pressed={annual}
                className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                  annual ? 'bg-[var(--lp-raise-2)] text-[var(--lp-ink)]' : 'text-[var(--lp-mid)]'
                }`}
              >
                Annual
                <span className="rounded bg-[var(--lp-accent-soft)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--lp-accent)]">
                  2 months free
                </span>
              </button>
            </div>
          </div>

          {/* ── Plans ────────────────────────────────────────────── */}
          <div className="mx-auto mt-12 grid max-w-6xl gap-5 px-5 lg:grid-cols-3">
            {PLANS.map(p => (
              <div
                key={p.name}
                className={`flex flex-col rounded-xl border p-7 ${
                  p.highlight ? 'border-[var(--lp-accent)] shadow-[var(--lp-shadow)]' : 'border-[var(--lp-grout)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-[15px] font-semibold text-[var(--lp-ink)]">{p.name}</h2>
                  {p.highlight && (
                    <span className="rounded-full bg-[var(--lp-accent-soft)] px-2.5 py-1 text-[11px] font-semibold text-[var(--lp-accent)]">
                      Most popular
                    </span>
                  )}
                </div>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="text-4xl font-semibold tracking-[-0.03em] text-[var(--lp-ink)]">
                    {annual ? p.price.annual : p.price.monthly}
                  </span>
                  {p.name !== 'Free' && (
                    <span className="text-[13px] text-[var(--lp-low)]">
                      /month{p.unit ? ` ${p.unit}` : ''}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[13px] text-[var(--lp-low)]">{p.note}</p>
                <button
                  onClick={onGoToSignUp}
                  className={`mt-6 w-full justify-center ${p.highlight ? 'btn-primary' : 'btn-outline'}`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-8 px-5 text-center text-[13px] text-[var(--lp-low)]">
            Prices in euros, tax included. Cancel any time and keep your data.
            14-day refund on every charge.
          </p>
        </section>

        {/* ── Full comparison ────────────────────────────────────── */}
        <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
              Everything, side by side
            </h2>

            {/* A wide table must scroll in its own container, never the page */}
            <div className="mt-10 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-[var(--lp-grout)]">
                    <th className="pb-3 pr-4 text-[13px] font-medium text-[var(--lp-low)]">&nbsp;</th>
                    {(['Free', 'Pro', 'Team'] as PlanKey[]).map(name => (
                      <th key={name} className="pb-3 text-center text-[13px] font-semibold text-[var(--lp-ink)]">
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(group => (
                    <React.Fragment key={group.group}>
                      <tr>
                        <td colSpan={4} className="pt-7 pb-2">
                          <span className="eyebrow" style={{ color: 'var(--t-teal)' }}>{group.group}</span>
                        </td>
                      </tr>
                      {group.items.map(row => (
                        <tr key={row.label} className="border-t border-[var(--lp-grout)]">
                          <td className="py-3 pr-4 text-[13.5px] text-[var(--lp-mid)]">{row.label}</td>
                          <td className="py-3 text-center"><Cell value={row.free} /></td>
                          <td className="py-3 text-center"><Cell value={row.pro} /></td>
                          <td className="py-3 text-center"><Cell value={row.team} /></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── Billing questions ──────────────────────────────────── */}
        <section className="border-b border-[var(--lp-grout)] py-20 sm:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="eyebrow" style={{ color: 'var(--t-gold)' }}>Billing</p>
              <h2 className="mt-3 text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
                The questions worth asking before you pay
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[var(--lp-mid)]">
                Anything else, email{' '}
                <a href="mailto:hello@mozayc.io" className="text-[var(--lp-accent)] hover:underline">
                  hello@mozayc.io
                </a>.
              </p>
            </div>

            <div className="divide-y divide-[var(--lp-grout)] border-y border-[var(--lp-grout)]">
              {BILLING_FAQ.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={f.q}>
                    <button
                      type="button" onClick={() => setOpenFaq(isOpen ? null : i)} aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 py-5 text-left"
                    >
                      <span className="text-[14.5px] font-medium text-[var(--lp-ink)]">{f.q}</span>
                      <ChevronDown className={`h-4 w-4 flex-none text-[var(--lp-low)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <p className="-mt-1 pb-5 pr-8 text-[13.5px] leading-relaxed text-[var(--lp-mid)]">{f.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Close ──────────────────────────────────────────────── */}
        <section className="border-b border-[var(--lp-grout)] py-20 sm:py-28">
          <div className="mx-auto max-w-2xl px-5 text-center">
            <h2 className="text-balance text-3xl font-semibold tracking-[-0.02em] text-[var(--lp-ink)] sm:text-4xl">
              Start with free
            </h2>
            <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-[var(--lp-mid)]">
              No card, no trial clock. Move up when you want to share your year
              with someone.
            </p>
            <button onClick={onGoToSignUp} className="btn-primary btn-lg group mt-8">
              Start free
              <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </LandingChrome>
  );
}
